/**
 * Floating add-to-cart bar.
 *
 * Shows once the main Add to cart button has scrolled out under the header
 * and hides when it returns. The bar's button presses the main button, so the
 * product form keeps full control of variants, selling plans and the drawer.
 * Label, disabled/loading state and price are mirrored from the main form.
 */
(() => {
  if (window.FFStickyAtc) return;

  const BODY_CLASS = 'ff-sticky-atc-on';

  class FFStickyAtc extends HTMLElement {
    #frame = 0;
    #onScroll = () => this.#queueCheck();
    #buttonWatcher = null;
    #priceWatcher = null;
    #unsubscribe = null;
    #observedButton = null;

    connectedCallback() {
      this.button = this.querySelector('[data-ff-satc-btn]');
      this.priceSlot = this.querySelector('[data-ff-satc-price]');
      if (!this.button) return;

      this.button.addEventListener('click', () => this.#press());
      this.#bind();

      // Measured on scroll rather than observed: an observer only reports
      // changes, so a jump straight past the button would never show the bar.
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      window.addEventListener('resize', this.#onScroll, { passive: true });
      this.#queueCheck();

      // A variant change can re-render parts of the section; re-attach afterwards.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.#unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () =>
          requestAnimationFrame(() => this.#bind())
        );
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.#onScroll);
      window.removeEventListener('resize', this.#onScroll);
      cancelAnimationFrame(this.#frame);
      this.#buttonWatcher?.disconnect();
      this.#priceWatcher?.disconnect();
      this.#unsubscribe?.();
      document.body.classList.remove(BODY_CLASS);
    }

    get #mainButton() {
      return document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
    }

    get #mainPrice() {
      return document.getElementById(`price-${this.dataset.section}`);
    }

    #bind() {
      const main = this.#mainButton;
      if (!main) return;

      if (main !== this.#observedButton) {
        this.#buttonWatcher?.disconnect();

        this.#buttonWatcher = new MutationObserver(() => this.#syncButton());
        this.#buttonWatcher.observe(main, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
        this.#observedButton = main;
      }

      const price = this.#mainPrice;
      this.#priceWatcher?.disconnect();
      if (price) {
        this.#priceWatcher = new MutationObserver(() => this.#syncPrice());
        this.#priceWatcher.observe(price, { childList: true, subtree: true, characterData: true });
      }

      this.#syncButton();
      this.#syncPrice();
      this.#queueCheck();
    }

    #queueCheck() {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        const main = this.#mainButton;
        if (!main) return;
        // Past the button means its bottom edge has gone under the sticky header.
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        this.#toggle(main.getBoundingClientRect().bottom < header);
      });
    }

    #toggle(show) {
      if (show === this.classList.contains('is-visible')) return;
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      document.body.classList.toggle(BODY_CLASS, show);
    }

    #syncButton() {
      const main = this.#mainButton;
      if (!main) return;
      const label = main.querySelector('span')?.textContent.trim() || main.textContent.trim();
      if (label && this.button.textContent.trim() !== label) this.button.textContent = label;
      this.button.disabled = main.disabled || main.getAttribute('aria-disabled') === 'true';
      this.button.classList.toggle('loading', main.classList.contains('loading'));
    }

    #syncPrice() {
      const price = this.#mainPrice;
      if (!price || !this.priceSlot) return;
      const onSale = price.querySelector('.price--on-sale');
      const current = onSale
        ? price.querySelector('.price__sale .price-item--sale')
        : price.querySelector('.price__regular .price-item--regular');
      const text = current?.textContent.trim();
      if (!text) return;

      const period = this.priceSlot.querySelector('.ff-price-period');
      this.priceSlot.textContent = text;
      if (period) this.priceSlot.append(period);
    }

    #press() {
      const main = this.#mainButton;
      if (!main || main.disabled) return;
      main.click();
    }
  }

  window.FFStickyAtc = FFStickyAtc;
  customElements.define('ff-sticky-atc', FFStickyAtc);
})();
